import React, { useMemo } from 'react';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import ITagDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { Panel } from './Panel/Panel';
import { TextButton } from '../buttons/TextButton';

interface Props {
  allTagGroups: ITagGroupDetail[];
  selectedTagIds: Guid[];
  onChange: (tagGuids: Guid[]) => void;
}

export const TagSelect = ({
  allTagGroups,
  selectedTagIds,
  onChange
}: Props) => {
  const tagsById = useMemo(() => {
    const map = new Map<string, ITagDetail>();
    allTagGroups.forEach((group) =>
      group.tags.forEach((tag) => {
        map.set(tag.id, tag);
      })
    );
    return map;
  }, [allTagGroups]);

  const tagGroupsWithUnselectedTags = useMemo(() => {
    return allTagGroups.map((group) => ({
      id: group.id,
      name: group.name,
      color: group.color,
      tags: group.tags.filter((tag) => !selectedTagIds.includes(tag.id))
    }));
  }, [allTagGroups, selectedTagIds]);

  const selectedTags = useMemo(() => {
    return selectedTagIds
      .map((id) => tagsById.get(id))
      .filter((t): t is ITagDetail => !!t);
  }, [selectedTagIds, tagsById]);

  const selectedTagsByGroupName = useMemo(() => {
    const grouped: Record<
      string,
      { tags: ITagDetail[]; color: string; id: Guid }
    > = {};
    selectedTags.forEach((tag) => {
      const group = allTagGroups.find((g) =>
        g.tags.some((t) => t.id === tag.id)
      );
      if (group) {
        if (!grouped[group.name]) {
          grouped[group.name] = {
            tags: [],
            id: group.id,
            color: group.color
          };
        }
        grouped[group.name].tags.push(tag);
      }
    });
    return grouped;
  }, [selectedTags, allTagGroups]);

  const handleAdd = (tag: ITagDetail) => {
    if (!selectedTagIds.includes(tag.id)) {
      onChange([...selectedTagIds, tag.id]);
    }
  };

  const handleRemove = (tag: ITagDetail) => {
    onChange(selectedTagIds.filter((id) => id !== tag.id));
  };

  return (
    <div className="tag-select-container">
      <div
        className="available-tags-container"
        data-testid="available-tags-container"
      >
        <Panel
          title="Find Tags"
          groups={tagGroupsWithUnselectedTags}
          onTagClick={handleAdd}
          iconClass="fa-light fa-circle-plus fa-lg"
        />
      </div>
      <div
        className="selected-tags-container"
        data-testid="selected-tags-container"
      >
        <Panel
          title="Selected Tags"
          groups={Object.entries(selectedTagsByGroupName).map(
            ([tagGroupName, tagGroup]) => ({
              id: tagGroup.id,
              name: tagGroupName,
              color: tagGroup.color,
              tags: tagGroup.tags
            })
          )}
          onTagClick={handleRemove}
          iconClass="fa-light fa-circle-minus fa-lg"
        />
        <div className="remove-all-container">
          <TextButton
            text="Remove All"
            type="primary-text-only"
            classNames="remove-all-button"
            onClick={() => onChange([])}
          />
        </div>
      </div>
    </div>
  );
};
